<template>
  <div
    class="flex items-center meridia-checkbox-wrapper"
    :class="[
      {
        disabled: disabled,
        'cursor-not-allowed': disabled,
        'cursor-pointer': !disabled,
      },
      getPtClass < PassThroughMeridiaCheckbox > ('wrapper', passThrough),
    ]"
  >
    <PrimeCheckbox
      v-if="defaultValue !== undefined"
      :defaultValue="defaultValue"
      binary
      unstyled
      :indeterminate="indeterminate"
      :pt="passThrough"
      :disabled="disabled"
      :input-id="computedId"
      class="meridia-checkbox"
      :class="sizeClass"
    >
      <template #icon="scope">
        <MeridiaIcon
          v-if="scope.checked"
          v-bind:class="scope.class"
          class="checkbox-icon"
          :name="icon"
          type="material-icons-outlined"
        />
        <MeridiaIcon
          v-else-if="scope.indeterminate"
          v-bind:class="scope.class"
          class="checkbox-icon"
          name="remove"
          type="material-icons-outlined"
        />
      </template>
    </PrimeCheckbox>
    <PrimeCheckbox
      v-else
      v-model="modelValue"
      binary
      unstyled
      :indeterminate="indeterminate"
      :pt="passThrough"
      :disabled="disabled"
      :input-id="computedId"
      class="meridia-checkbox"
      :class="sizeClass"
    >
      <template #icon="scope">
        <MeridiaIcon
          v-if="scope.checked"
          v-bind:class="scope.class"
          class="checkbox-icon"
          :name="icon"
          type="material-icons-outlined"
        />
        <MeridiaIcon
          v-else-if="scope.indeterminate"
          v-bind:class="scope.class"
          class="checkbox-icon"
          name="remove"
          type="material-icons-outlined"
        />
      </template>
    </PrimeCheckbox>
    <label
      v-if="label || $slots.label"
      :for="computedId"
      class="checkbox-label"
      :class="getPtClass < PassThroughMeridiaCheckbox > ('label', passThrough)"
    >
      <slot v-if="$slots.label" name="label" :label="label" />
      <div v-else>{{ label }}</div>
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { CheckboxSize, PassThroughMeridiaCheckbox } from './types';
import { usePassThrough } from 'primevue/passthrough';
import _ from 'lodash';
import { getPtClass } from '../utils';
import PrimeCheckbox from 'primevue/checkbox';
import MeridiaIcon from '../icon/MeridiaIcon.vue';

const modelValue = defineModel<boolean>();

const props = withDefaults(
  defineProps<{
    size?: `${CheckboxSize}`;
    defaultValue?: boolean;
    label?: string;
    disabled?: boolean;
    icon?: string;
    pt?: PassThroughMeridiaCheckbox;
    indeterminate?: boolean;
  }>(),
  {
    size: CheckboxSize.BIG,
    disabled: false,
    icon: 'checked',
    defaultValue: undefined,
    indeterminate: false,
  }
);

defineEmits<{
  (event: 'update:model-value', value: boolean): void;
}>();

defineSlots<{
  label: (label?: string) => any;
}>();

const computedId = computed(() => _.uniqueId('checkbox-'));

const sizeClass = computed(() => ({
  'size-small': props.size === CheckboxSize.SMALL,
  'size-big': props.size === CheckboxSize.BIG,
}));

const passThrough = usePassThrough(
  props.pt,
  {
    input: () => ({
      class: {
        'checkbox-input': true,
      },
    }),
    box: (options) => ({
      class: {
        'checkbox-box': true,
        disabled: options.context.disabled,
        checked: options.context.checked || options.context.indeterminate,
      },
    }),
  } as PassThroughMeridiaCheckbox,
  { mergeSections: true, mergeProps: true }
) as PassThroughMeridiaCheckbox;
</script>

<style lang="scss">
.meridia-checkbox-wrapper {
  .checkbox-label {
    @apply text-neutral-text-body text-body-base cursor-pointer pl-xs flex items-center flex-1 transition-all duration-200;
  }

  .meridia-checkbox {
    @apply relative inline-flex;

    .checkbox-input {
      @apply appearance-none cursor-pointer absolute top-0 w-full h-full p-0 m-0 opacity-0 z-[1] outline-none border-0;
    }
    .checkbox-icon {
      @apply w-full h-full overflow-hidden transition-all duration-200 text-white;
    }
    &.size-small {
      @apply w-md h-md;
      .checkbox-icon {
        font-size: 12px;
      }
    }
    &.size-big {
      @apply w-xl h-xl;
      .checkbox-icon {
        font-size: 20px;
      }
    }
    .checkbox-box {
      @apply w-[inherit] h-[inherit] flex justify-center items-center radius-small transition-all duration-200 border-2;
    }
    .checkbox-box:not(.checked) {
      @apply border-neutral-border-darker;
    }
    .checkbox-box.checked {
      @apply border-primary-border-default bg-primary-surface-default;
    }
  }

  &.disabled {
    @apply opacity-40;
    .checkbox-input {
      @apply cursor-not-allowed;
    }

    .checkbox-label {
      @apply text-neutral-text-disabled cursor-not-allowed;
    }
  }

  &:not(.disabled):hover {
    .checkbox-box {
      @apply border-primary-border-default;
    }
    .checkbox-box.checked {
      @apply border-primary-border-darker bg-primary-surface-darker;
    }
  }

  &:not(.disabled):focus {
    .checkbox-box {
      box-shadow: 0px 0px 0px 2px rgba(167, 241, 206, 0.4);
    }
  }
}
</style>
